import React from 'react';
import { motion } from 'framer-motion';

const Terms = () => {
  return (
    <div className="min-h-screen bg-black py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#121214] border border-white/10 rounded-2xl p-8 md:p-12"
        >
          <h1 className="text-4xl sm:text-5xl font-medium mb-6 tracking-tight">Terms & Conditions</h1>
          <div className="prose prose-invert max-w-none">
            <div className="space-y-6 text-[#A1A1A6] leading-relaxed">
              <p className="text-lg">
                Welcome to MyGaadiNumber. These Terms and Conditions govern your use of our platform and services. By registering for and using MyGaadiNumber, you agree to be bound by these terms. Please read them carefully before proceeding.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing, browsing, or using the MyGaadiNumber platform, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions, as well as our Privacy & Security Policy. If you do not agree to these terms, you must not use our services.
              </p>
              <p>
                These terms constitute a legally binding agreement between you (the "User") and MyGaadiNumber (the "Company," "we," "us," or "our"). Your use of the platform signifies your acceptance of these terms.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">2. Service Description</h2>
              <p>
                MyGaadiNumber is an emergency blood group information platform that allows users to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Register their blood group information along with their vehicle registration numbers</li>
                <li>Store family members' blood group information (up to 3 members)</li>
                <li>Search for blood group information using vehicle registration numbers in emergency situations</li>
                <li>Manage and update their profile information</li>
                <li>Participate in a referral program to earn rewards</li>
                <li>Manage wallet and withdrawal requests</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">3. User Eligibility</h2>
              <p>
                To use MyGaadiNumber, you must:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Be at least 18 years of age</li>
                <li>Have the legal capacity to enter into a binding contract</li>
                <li>Provide accurate and complete registration information</li>
                <li>Own or have legal authority over the vehicles you register</li>
                <li>Maintain the confidentiality of your account credentials</li>
              </ul>
              <p>
                Users under 18 may be registered as family members by their parent or legal guardian who holds a valid account.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">4. Registration and Account Security</h2>
              <p>
                When you create an account on MyGaadiNumber, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide true, accurate, current, and complete information</li>
                <li>Maintain and promptly update your information to keep it accurate</li>
                <li>Keep your password secure and confidential</li>
                <li>Accept responsibility for all activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
              <p>
                We reserve the right to suspend or terminate accounts that provide false, inaccurate, or misleading information.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">5. Subscription and Payment Terms</h2>
              <p>
                MyGaadiNumber operates on a lifetime subscription model with the following terms:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Subscription Fee:</strong> ₹150 for lifetime access to all platform features</li>
                <li><strong>Referral Discount:</strong> ₹25 discount when you use a valid referral code during registration</li>
                <li><strong>One-Time Payment:</strong> This is a one-time payment with no recurring charges</li>
                <li><strong>Profile Updates:</strong> You may update your information unlimited times at no additional cost</li>
                <li><strong>Payment Gateway:</strong> Payment processing will be through integrated payment gateways (coming soon)</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">6. Refund and Cancellation Policy</h2>
              <p className="text-[#FF3B30] font-medium">
                IMPORTANT: There are NO REFUNDS after completing registration and payment.
              </p>
              <p>
                Once you have completed your registration and the payment has been processed, the subscription fee is non-refundable. This policy is in place because:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You receive lifetime access immediately upon payment</li>
                <li>Your information is instantly added to our searchable database</li>
                <li>The subscription is a one-time payment with no recurring charges</li>
                <li>You have unlimited access to update your profile at any time</li>
              </ul>
              <p>
                However, you retain the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Profile Modification:</strong> You can modify your details anytime without additional payment</li>
                <li><strong>Account Cancellation:</strong> You may request account cancellation by contacting us at +91 9014556536</li>
                <li><strong>Data Deletion:</strong> Upon cancellation, your data will be removed from our system within 30 days</li>
              </ul>
              <p>
                <strong>For cancellation-related queries, please contact us at +91 9014556536.</strong>
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">7. Referral Program Terms</h2>
              <p>
                The MyGaadiNumber referral program operates under the following rules:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Referrer Reward:</strong> Earn ₹50 in your wallet when someone uses your referral code and completes payment</li>
                <li><strong>Referee Benefit:</strong> Get ₹25 discount on the subscription fee when using a referral code</li>
                <li><strong>Unique Code:</strong> Each user receives a unique referral code upon registration</li>
                <li><strong>Withdrawal Minimum:</strong> You can withdraw funds only when your wallet balance reaches ₹100 or more</li>
                <li><strong>Payment Method:</strong> Withdrawals are processed manually via UPI within 24-48 hours</li>
                <li><strong>Fraud Prevention:</strong> We reserve the right to cancel referral rewards if fraudulent activity is detected</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">8. User Responsibilities and Obligations</h2>
              <p>
                As a user of MyGaadiNumber, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Provide Accurate Information:</strong> Ensure all blood group information, vehicle details, and personal data are correct and current</li>
                <li><strong>Regular Updates:</strong> Update your information if there are any changes in your medical status, vehicle ownership, or contact details</li>
                <li><strong>Lawful Use:</strong> Use the platform only for legitimate emergency information purposes</li>
                <li><strong>No Misuse:</strong> Not use the platform to harass, stalk, or invade the privacy of others</li>
                <li><strong>Respect Privacy:</strong> Use emergency search information only for genuine emergency purposes</li>
                <li><strong>Accurate Searches:</strong> Search for vehicle numbers only in legitimate emergency situations</li>
                <li><strong>No Spam:</strong> Not engage in spamming or fraudulent referral activities</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">9. Disclaimers and Limitations of Liability</h2>
              <p className="text-[#FF9F0A] font-medium">
                PLEASE READ THIS SECTION CAREFULLY AS IT LIMITS OUR LIABILITY
              </p>
              <p>
                <strong>9.1 No Responsibility for False Information:</strong> MyGaadiNumber is NOT responsible for any false, inaccurate, or misleading information provided by users. We act as a platform for information storage and retrieval; we do not verify the accuracy of user-submitted data.
              </p>
              <p>
                <strong>9.2 No Medical Advice:</strong> The platform provides blood group information only. It does not constitute medical advice, diagnosis, or treatment. Always consult qualified medical professionals for medical decisions.
              </p>
              <p>
                <strong>9.3 No Guarantee of Availability:</strong> While we strive for maximum uptime, we do not guarantee that the platform will be available 100% of the time. We are not responsible if someone cannot access information due to technical issues, internet connectivity problems, or user account issues.
              </p>
              <p>
                <strong>9.4 Emergency Limitations:</strong> We are not responsible if someone is unaware of our platform during an emergency or if the person involved has not registered their information with us.
              </p>
              <p>
                <strong>9.5 Verification Responsibility:</strong> Users searching for emergency information should verify the blood group with medical professionals before any medical procedure. We are not liable for any consequences of using unverified information.
              </p>
              <p>
                <strong>9.6 Third-Party Actions:</strong> We are not responsible for the actions of users who access information through our platform, including any misuse of information obtained through emergency searches.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">10. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>MyGaadiNumber shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Our total liability shall not exceed the amount you paid for the subscription (₹150 or ₹125)</li>
                <li>We are not liable for any loss of data, profits, revenue, or business opportunities</li>
                <li>We are not liable for delays or failures due to circumstances beyond our reasonable control</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">11. Intellectual Property Rights</h2>
              <p>
                All content on the MyGaadiNumber platform, including but not limited to text, graphics, logos, icons, images, and software, is the property of MyGaadiNumber and is protected by intellectual property laws. You may not:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Copy, reproduce, or distribute any platform content without permission</li>
                <li>Reverse engineer or attempt to extract the source code</li>
                <li>Use our trademarks or branding without authorization</li>
                <li>Create derivative works based on our platform</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">12. Prohibited Activities</h2>
              <p>
                You agree not to engage in any of the following prohibited activities:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violating any laws or regulations</li>
                <li>Impersonating another person or entity</li>
                <li>Uploading malicious code or viruses</li>
                <li>Attempting to gain unauthorized access to our systems</li>
                <li>Interfering with the proper functioning of the platform</li>
                <li>Harvesting or collecting user information without consent</li>
                <li>Using automated systems or bots to access the platform</li>
                <li>Engaging in fraudulent referral activities</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">13. Account Termination</h2>
              <p>
                We reserve the right to suspend or terminate your account if:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You violate these Terms and Conditions</li>
                <li>You provide false or misleading information</li>
                <li>You engage in fraudulent or illegal activities</li>
                <li>You abuse the referral system</li>
                <li>Your account shows suspicious activity</li>
              </ul>
              <p>
                Upon termination, you will lose access to your account and any accumulated wallet balance. Termination does not entitle you to a refund of the subscription fee.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">14. Consent to Information Sharing</h2>
              <p>
                By registering on MyGaadiNumber, you explicitly consent to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The sharing of your blood group information with authenticated users during emergency searches</li>
                <li>The display of your name, photo, alternative contact number, and vehicle details in search results</li>
                <li>The sharing of your family members' blood group information as part of your profile</li>
              </ul>
              <p>
                This consent is essential for the platform to function. If you do not agree, please do not register.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">15. Reporting Abuse and Contact for Issues</h2>
              <p>
                If you encounter spam, abuse, or suspicious activity on the platform, or if you have any questions or concerns, please contact us immediately:
              </p>
              <p className="text-white">
                <strong>Phone:</strong> +91 9014556536<br />
                <strong>Address:</strong> MyGaadiNumber, Door No. 302, Chaimina Enclave, AS Raju Nagar, Kukatpally, Hyderabad, Telangana - 500072, India
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">16. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless MyGaadiNumber, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses arising from:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your use of the platform</li>
                <li>Your violation of these Terms and Conditions</li>
                <li>Your violation of any third-party rights</li>
                <li>Any false or inaccurate information you provide</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">17. Modifications to Terms</h2>
              <p>
                We reserve the right to modify these Terms and Conditions at any time. We will notify users of significant changes via email or through a prominent notice on the platform. Your continued use of MyGaadiNumber after such changes constitutes acceptance of the modified terms.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">18. Governing Law and Jurisdiction</h2>
              <p>
                These Terms and Conditions are governed by the laws of India. Any disputes arising from or relating to these terms or your use of MyGaadiNumber shall be subject to the exclusive jurisdiction of the courts in Hyderabad, Telangana, India.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">19. Severability</h2>
              <p>
                If any provision of these Terms and Conditions is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">20. Entire Agreement</h2>
              <p>
                These Terms and Conditions, together with our Privacy & Security Policy, constitute the entire agreement between you and MyGaadiNumber regarding the use of our platform and supersede all prior agreements and understandings.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">21. Contact Information</h2>
              <p>
                For any questions about these Terms and Conditions, please contact us:
              </p>
              <p className="text-white">
                <strong>Phone:</strong> +91 9014556536<br />
                <strong>Business Address:</strong> MyGaadiNumber<br />
                Door No. 302, Chaimina Enclave<br />
                AS Raju Nagar, Kukatpally<br />
                Hyderabad, Telangana - 500072<br />
                India
              </p>

              <p className="text-white font-medium mt-8">
                By using MyGaadiNumber, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>

              <p className="text-white font-medium mt-4">
                Last Updated: January 2026
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Terms;